// src/pages/Workflow.js – New Workflow page component (replacing TestPage)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Navbar, Nav, Form, Modal, Dropdown, Alert, Badge, InputGroup, ProgressBar } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';  // (No longer used for node order, but could remove)
import ReactFlow, { ReactFlowProvider, addEdge, Background, Controls, MarkerType } from 'reactflow';
import 'reactflow/dist/style.css';
import '../styles/Workflow.css';  // Contains styles for layout and module items
import { FaFile, FaBook, FaDatabase, FaExchangeAlt, FaCheckCircle, FaChartPie, FaLanguage, FaSearch, FaInfoCircle, FaUser } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import Select from 'react-select';
import { useNotification } from '../contexts/NotificationContext';
import CloudSpaceNew from './cloud/CloudSpaceNew';
import LanguageSwitcher from './common/LanguageSwitcher';
import PromptManagerModal from './prompt/PromptManagerModal';
import { WorkflowExecutionService } from '../services/workflowService';
import { promptsAPI, userAPI, workflowAPI } from '../utils/httpClient';
import WorkflowStatusPanel from './workflow/WorkflowStatusPanel';
import useResizablePanels from '../hooks/useResizablePanels';
import ResizableDivider from './ResizableDivider';
import { useI18n } from '../i18n';
import UserProfileModal from './user/UserProfileModal';
import TaskManagerModal from './task/TaskManagerModal';
import { useVersionCheck } from '../hooks/useVersionCheck';
import VersionUpdateModal from './version/VersionUpdateModal';

//------------------------------------------------------
// CONSTANTS AND CONFIGURATIONS
//------------------------------------------------------

// Define module types and their metadata
const moduleTypes = [
    {
        id: 'fileInput',
        name: 'File Input',
        description: 'Upload source file and set languages',
        icon: <FaFile />,
        requires: [],
        allowMultiple: true,
        maxPerPipeline: 1
    },
    {
        id: 'termBase',
        name: 'Term Base',
        description: 'Optional terminology database',
        icon: <FaBook />,
        requires: ['fileInput'],
        allowMultiple: true,
        maxPerPipeline: 1
    },
    {
        id: 'memoryBase',
        name: 'Memory Base',
        description: 'Optional translation memory',
        icon: <FaDatabase />,
        requires: ['fileInput'],
        allowMultiple: true,
        maxPerPipeline: 1
    },
    {
        id: 'translation',
        name: 'Translation',
        description: 'Machine translation of text',
        icon: <FaExchangeAlt />,
        requires: ['fileInput'],
        allowMultiple: true,
        maxPerPipeline: 2
    },
    {
        id: 'review',
        name: 'Review',
        description: 'Review/QA for translation output',
        icon: <FaCheckCircle />,
        requires: ['translation'],
        allowMultiple: true,
        maxPerPipeline: 2
    },
    {
        id: 'summary',
        name: 'Summary',
        description: 'Summarize or merge multiple outputs',
        icon: <FaChartPie />,
        requires: ['translation'],
        allowMultiple: true,
        maxPerPipeline: 1
    }
];

// Create more comprehensive language options
const languageOptions = [
    { value: 'zh-cn', label: 'Chinese (Simplified)' },
    { value: 'zh-tw', label: 'Chinese (Traditional)' },
    { value: 'en-us', label: 'English (US)' },
    { value: 'ko', label: 'Korean' },
    { value: 'ja', label: 'Japanese' },
    { value: 'th', label: 'Thai' },
    { value: 'id', label: 'Indonesian' },
    { value: 'vi', label: 'Vietnamese' },
    { value: 'tr', label: 'Turkish' },
    { value: 'it', label: 'Italian' },
    { value: 'de', label: 'German' },
    { value: 'fr', label: 'French' },
    { value: 'ru', label: 'Russian' },
    { value: 'es-es', label: 'Spanish (Spain)' },
    { value: 'es-la', label: 'Spanish (Latin America)' },
    { value: 'pt-pt', label: 'Portuguese' },
    { value: 'pt-br', label: 'Portuguese (Brazil)' }
];

// Model parameter presets
const modelPresets = {
    creative: {
        temperature: 0.8,
        top_p: 0.9,
        presence_penalty: 0.1,
        frequency_penalty: 0.1
    },
    balanced: {
        temperature: 0.5,
        top_p: 0.85,
        presence_penalty: 0.2,
        frequency_penalty: 0.3
    },
    precise: {
        temperature: 0.2,
        top_p: 0.75,
        presence_penalty: 0.5,
        frequency_penalty: 0.5
    }
};


// -----------------------------------------------------
// PRICING AND COST ESTIMATION (frontend-only quick view)
// -----------------------------------------------------

// Per 1K token pricing (USD) aligned with backend pricing.py.
// Two rates vary with time and must stay in sync with that module:
//   - DeepSeek bills peak/off-peak by UTC time-of-day (off-peak is half price).
//   - Gemini 3.8 Flash launch pricing doubles on 2027-01-01.
const PRICING_PER_1K = {
    openai: {
        'gpt-6-sol': { in: 0.00200, out: 0.01000 },
        'gpt-6-luna': { in: 0.00010, out: 0.00050 },
        // Superseded, kept so saved workflows still show an estimate.
        'gpt-5.6-sol': { in: 0.00400, out: 0.02000 },
        'gpt-5.6-luna': { in: 0.00020, out: 0.00120 },
        'gpt-5.4': { in: 0.0025, out: 0.0150 },
        'gpt-5.4-mini': { in: 0.00075, out: 0.0045 },
        'gpt-5.4-nano': { in: 0.00020, out: 0.00125 },
    },
    gemini: {
        'gemini-3.1-pro-preview': { in: 0.00200, out: 0.01200 },
        'gemini-3.1-flash-lite-preview': { in: 0.00025, out: 0.00150 },
    },
    deepseek: {},
    anthropic: {
        'claude-opus-5-5': { in: 0.004, out: 0.020 },
        'claude-sonnet-5-5': { in: 0.002, out: 0.010 },
        'claude-haiku-5-5': { in: 0.0001, out: 0.0005 },
    },
};

// Ordered by effective date; the last tier whose date has arrived wins.
const GEMINI_PRICE_SCHEDULE = {
    'gemini-3.8-flash': [
        { from: 0, in: 0.00075, out: 0.00375 },
        { from: Date.UTC(2027, 0, 1), in: 0.00150, out: 0.00750 },
    ],
};

// DeepSeek PEAK rates. Peak hours are Monday-Friday in UTC, half-open [start, end).
const DEEPSEEK_PEAK_PRICING = {
    'deepseek-v4-flash': { in: 0.00044, out: 0.00132 },
    'deepseek-v4-pro': { in: 0.00132, out: 0.00396 },
};
const DEEPSEEK_PEAK_WINDOWS_UTC = [[1, 4], [6, 10]];
const DEEPSEEK_OFF_PEAK_MULTIPLIER = 0.5;

const providerByModel = (model) => {
    const m = String(model || '').toLowerCase();
    if (m.startsWith('gpt') || m === 'o3') return 'openai';
    if (m.startsWith('gemini')) return 'gemini';
    if (m.startsWith('deepseek')) return 'deepseek';
    if (m.startsWith('claude')) return 'anthropic';
    return null;
};

const isDeepSeekPeak = (at) => {
    const day = at.getUTCDay(); // 0 = Sunday, 6 = Saturday
    if (day === 0 || day === 6) return false;
    const hour = at.getUTCHours();
    return DEEPSEEK_PEAK_WINDOWS_UTC.some(([start, end]) => hour >= start && hour < end);
};

const getPricingForModel = (model, at = new Date()) => {
    const provider = providerByModel(model);
    if (!provider) return null;

    if (provider === 'deepseek') {
        const peak = DEEPSEEK_PEAK_PRICING[model];
        if (!peak) return null;
        if (isDeepSeekPeak(at)) return peak;
        return {
            in: peak.in * DEEPSEEK_OFF_PEAK_MULTIPLIER,
            out: peak.out * DEEPSEEK_OFF_PEAK_MULTIPLIER,
        };
    }

    if (provider === 'gemini') {
        const schedule = GEMINI_PRICE_SCHEDULE[model];
        if (schedule) {
            const now = at.getTime();
            const active = schedule.filter((tier) => now >= tier.from).pop();
            return active ? { in: active.in, out: active.out } : null;
        }
    }

    return PRICING_PER_1K[provider]?.[model] || null;
};

// Rough cost estimate for 100 rows, using default template assumptions
// - avgChars: default average characters per row (rough)
// - For translation: output tokens ~= 1.1x input tokens
// - For review: fixed short output per row
const estimateCostPer100Rows = ({ model, moduleType = 'translation', avgChars = 170 }) => {
    const pricing = getPricingForModel(model);
    if (!pricing) return null;

    // simple char->token heuristic (mixed languages)
    const approxTokensPerChar = 0.8; // conservative average
    const sourceTokens = Math.max(1, Math.round(avgChars * approxTokensPerChar));

    // default template base prompt tokens (instructions, rules, placeholders)
    const basePromptTokens = moduleType === 'review' ? 200 : 250;

    // New estimation rule:
    // - input tokens: (base + source) * 3
    // - translation output: source * 1.5
    // - review output: source * 5
    const promptTokens = Math.round((basePromptTokens + sourceTokens) * 4);
    const completionTokens = moduleType === 'review'
        ? Math.round(sourceTokens * 6)
        : Math.round(sourceTokens * 3);

    const inCost = (promptTokens / 1000) * pricing.in;
    const outCost = (completionTokens / 1000) * pricing.out;
    const perLine = inCost + outCost;
    const per100 = perLine * 100;

    return { per100USD: per100 };
};



//------------------------------------------------------
// HELPER FUNCTIONS
//------------------------------------------------------

// Helper to get available columns from module properties
const getAvailableColumns = (moduleProps) => {
    if (moduleProps.availableColumns && moduleProps.availableColumns.length > 0) {
        return moduleProps.availableColumns;
    }
    if (moduleProps.columns && moduleProps.columns.length > 0) {
        return moduleProps.columns;
    }
    return [];
};

// Helper to get default properties for each module type
// claudeUsedUp: once the shared Claude credit is used up, new translation/review modules
// default to OpenAI (GPT-6 Luna / GPT-6 Sol) instead of Claude.
const getDefaultProperties = (type, claudeUsedUp = false) => {
    switch (type) {
        case 'fileInput':
            return {
                displayName: '',
                gameType: '',
                story: '',
                textType: 'dialogue',
                sourceLang: '',
                targetLangs: [],    // will hold an array of codes
                sourceColumn: '',
                fileMode: 'translation', // 'translation' or 'calibration'
                targetColumnMappings: {}, // For calibration mode: { langCode: columnName }
                file: null,         // will hold the File object for local upload
                cloudFile: null,    // will hold the cloud file reference
                fileSource: 'local', // 'local' or 'cloud'
                columns: []         // will hold column data from file
            };
        case 'termBase':
            return {
                displayName: '',
                cloudFile: null,  // Store selected cloud file object
                sourceColumn: '',  // This will map to vectorized_source_column
                targetColumns: [],  // This will map to vectorized_target_columns
                selectedCombination: ''  // Track selected vectorization combination
            };
        case 'memoryBase':
            return {
                displayName: '',
                cloudFile: null,  // Store selected cloud file object
                sourceColumn: '',  // This will map to vectorized_source_column
                targetColumns: [],  // This will map to vectorized_target_columns
                selectedCombination: ''  // Track selected vectorization combination
            };
        case 'translation':
            return {
                displayName: '',
                model: claudeUsedUp ? 'gpt-6-luna' : 'claude-sonnet-5-5',
                temperature: 0.5,
                top_p: 0.85,
                presence_penalty: 0.0,
                frequency_penalty: 0.0,
                prompt: ''  // custom prompt (optional); will be auto-filled from backend default if empty
            };
        case 'review':
            return {
                displayName: '',
                model: claudeUsedUp ? 'gpt-6-sol' : 'claude-opus-5-5',
                temperature: 0.5,
                top_p: 0.85,
                presence_penalty: 0.0,
                frequency_penalty: 0.0,
                prompt: '' // will be auto-filled from backend default if empty
            };
        case 'summary':
            return {
                displayName: '',
                model: 'gpt-5.4',
                temperature: 0.5,
                top_p: 0.85,
                presence_penalty: 0.0,
                frequency_penalty: 0.0,
                prompt: '' // will be auto-filled from backend default if empty
            };
        default:
            return {};
    }
};

// Helper to convert File to base64 string
const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        if (!file) {
            resolve(null);
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result;
            // Remove the data URL prefix to send only base64 string
            const base64 = result?.toString().split(',')[1];
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

//------------------------------------------------------
// SUB-COMPONENTS
//------------------------------------------------------

// Helper component for labels with tooltips
const LabelWithTooltip = ({ children, tooltip, required = false, className = "" }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const iconRef = useRef(null);

    const handleMouseEnter = () => {
        if (iconRef.current) {
            const rect = iconRef.current.getBoundingClientRect();
            setTooltipPosition({
                top: rect.top - 10, // 10px above the icon
                left: rect.left + rect.width / 2 // centered on the icon
            });
        }
        setShowTooltip(true);
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
    };

    return (
        <>
            <Form.Label className={`d-flex align-items-center ${className}`}>
                {children}
                {required && <span className="text-danger ms-1">*</span>}
                <div
                    ref={iconRef}
                    className="ms-2"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    style={{ cursor: 'help' }}
                >
                    <FaInfoCircle size={12} style={{ color: '#6c757d' }} />
                </div>
            </Form.Label>
            {showTooltip && (
                <div
                    className="position-fixed bg-dark text-white p-2 rounded shadow"
                    style={{
                        top: `${tooltipPosition.top}px`,
                        left: `${tooltipPosition.left}px`,
                        transform: 'translate(-50%, -100%)',
                        minWidth: '200px',
                        maxWidth: '300px',
                        zIndex: 10000,
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                        pointerEvents: 'none' // Prevent tooltip from interfering with mouse events
                    }}
                >
                    {tooltip}
                    <div
                        className="position-absolute"
                        style={{
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 0,
                            height: 0,
                            borderLeft: '5px solid transparent',
                            borderRight: '5px solid transparent',
                            borderTop: '5px solid #000'
                        }}
                    />
                </div>
            )}
        </>
    );
};

// Update the PromptModal to be editable
const PromptModal = ({ show, onHide, title, content, onSave }) => {
    const [editedContent, setEditedContent] = useState(content);
    const { t } = useI18n();

    useEffect(() => {
        setEditedContent(content);
    }, [content]);

    const handleSave = () => {
        onSave(editedContent);
        onHide();
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            className="prompt-modal"
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Control
                    as="textarea"
                    rows={15}
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
                />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>{t('workflow:modal.cancel')}</Button>
                <Button variant="primary" onClick={handleSave}>{t('workflow:modal.save')}</Button>
            </Modal.Footer>
        </Modal>
    );
};

// Component for model-based modules settings
const ModelSettingsPanel = ({
    moduleId,
    moduleType,
    moduleProps,
    updateProperty,
    handleOpenPromptModal,
    applyModelPreset
}) => {
    const { t } = useI18n();
    const [claudeBudget, setClaudeBudget] = useState(null);
    useEffect(() => {
        let cancelled = false;
        workflowAPI.getClaudeBudget()
            .then((resp) => { if (!cancelled && resp && resp.success) setClaudeBudget(resp.data); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [moduleId]);
    const claudeUsedUp = Boolean(claudeBudget && claudeBudget.exhausted);
    const claudeResetsOn = claudeBudget ? String(claudeBudget.resets_at).slice(0, 10) : '';
    // Floor to cents like the Console's balance, so it never shows more than is left
    const claudeLeftUsd = claudeBudget ? Math.floor(Number(claudeBudget.remaining_usd) * 100) / 100 : 0;
    const claudeLeftPercent = claudeBudget && Number(claudeBudget.budget_usd) > 0
        ? Math.min(100, Math.max(0, (claudeLeftUsd / Number(claudeBudget.budget_usd)) * 100))
        : 0;
    const pricing = getPricingForModel(moduleProps.model);
    const estimate100 = estimateCostPer100Rows({ model: moduleProps.model, moduleType });
    return (
        <>
            <Form.Group className="mb-3">
                <LabelWithTooltip tooltip={t('workflow:tooltips.displayName')}>
                    {t('workflow:modelPanel.displayName')}
                </LabelWithTooltip>
                <Form.Control
                    type="text"
                    value={moduleProps.displayName || ''}
                    onChange={(e) => updateProperty(moduleId, 'displayName', e.target.value)}
                    placeholder={t('workflow:modelPanel.displayName')}
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <LabelWithTooltip tooltip={t('workflow:tooltips.model')}>
                    {t('workflow:modelPanel.model')}
                </LabelWithTooltip>
                <Form.Select
                    value={moduleProps.model || 'gpt-5.4-mini'}
                    onChange={(e) => updateProperty(moduleId, 'model', e.target.value)}
                >
                    {/* Anthropic Claude Models */}
                    <optgroup label={claudeUsedUp ? `Anthropic Claude (${t('workflow:modelPanel.claudeUsedUpShort')})` : 'Anthropic Claude'}>
                        <option value="claude-opus-5-5" disabled={claudeUsedUp}>Claude Opus 5.5</option>
                        <option value="claude-sonnet-5-5" disabled={claudeUsedUp}>Claude Sonnet 5.5</option>
                        <option value="claude-haiku-5-5" disabled={claudeUsedUp}>Claude Haiku 5.5</option>
                    </optgroup>
                    {/* OpenAI Models */}
                    <optgroup label="OpenAI">
                        <option value="gpt-6-sol">GPT-6 Sol</option>
                        <option value="gpt-6-luna">GPT-6 Luna</option>
                        <option value="gpt-5.4-mini">GPT-5.4 Mini</option>
                        <option value="gpt-5.4">GPT-5.4</option>
                        <option value="gpt-5.4-nano">GPT-5.4 Nano</option>
                    </optgroup>
                    {/* Google Gemini Models */}
                    <optgroup label="Google Gemini">
                        <option value="gemini-3.8-flash">Gemini 3.8 Flash</option>
                        <option value="gemini-3.1-pro-preview">Gemini 3.1 Pro Preview</option>
                        <option value="gemini-3.1-flash-lite-preview">Gemini 3.1 Flash-Lite Preview</option>
                    </optgroup>
                    {/* DeepSeek Models */}
                    <optgroup label="DeepSeek">
                        <option value="deepseek-v4-flash">DeepSeek V4 Flash</option>
                        <option value="deepseek-v4-pro">DeepSeek V4 Pro</option>
                    </optgroup>
                </Form.Select>
                {(pricing || estimate100) && (moduleType === 'translation' || moduleType === 'review') && (
                    <div className="mt-1 text-muted" style={{ fontSize: '0.85rem' }}>
                        {estimate100 && (
                            <>
                                {t('workflow:modelPanel.costEstimate100', { value: (estimate100.per100USD.toFixed(2)) })}
                                <span className="ms-2">(
                                    {moduleType === 'review' ? t('workflow:modelPanel.reviewEstimateLabel') : t('workflow:modelPanel.translationEstimateLabel')}
                                    )</span>
                            </>
                        )}
                    </div>
                )}
            </Form.Group>

            {moduleProps.model && moduleProps.model.toLowerCase().startsWith('gemini') && (
                <Alert variant="info" className="mb-3">
                    <div
                        style={{ fontSize: '0.9rem' }}
                        dangerouslySetInnerHTML={{ __html: t('workflow:modelPanel.geminiRecommendation') }}
                    />
                </Alert>
            )}

            {moduleProps.model && moduleProps.model.toLowerCase().startsWith('deepseek') && (
                <Alert variant="info" className="mb-3">
                    <div
                        style={{ fontSize: '0.9rem' }}
                        dangerouslySetInnerHTML={{ __html: t('workflow:modelPanel.deepseekPeakPricing') }}
                    />
                </Alert>
            )}

            {moduleProps.model && moduleProps.model.toLowerCase().startsWith('claude') && claudeBudget && (
                <Alert variant={claudeUsedUp ? 'warning' : 'info'} className="mb-3">
                    <div style={{ fontSize: '0.9rem' }}>
                        {claudeUsedUp
                            ? t('workflow:modelPanel.claudeBudgetUsedUp', { date: claudeResetsOn })
                            : t('workflow:modelPanel.claudeBudgetRemaining', {
                                remaining: claudeLeftUsd.toFixed(2),
                                budget: Number(claudeBudget.budget_usd).toFixed(0),
                                date: claudeResetsOn,
                            })}
                    </div>
                    <ProgressBar
                        className="mt-2"
                        style={{ height: '8px' }}
                        now={claudeUsedUp ? 0 : claudeLeftPercent}
                        variant={claudeLeftPercent > 50 && !claudeUsedUp ? 'success' : claudeLeftPercent > 20 && !claudeUsedUp ? 'warning' : 'danger'}
                        aria-label={t('workflow:modelPanel.claudeBudgetBar')}
                    />
                </Alert>
            )}

            <Form.Group className="mb-3">
                <LabelWithTooltip tooltip={t('workflow:tooltips.presets')}>
                    {t('workflow:modelPanel.presetsTitle')}
                </LabelWithTooltip>
                <div className="d-flex gap-2 mb-2">
                    <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => applyModelPreset(moduleId, 'creative')}
                    >
                        {t('workflow:modelPanel.presets.creative')}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => applyModelPreset(moduleId, 'balanced')}
                    >
                        {t('workflow:modelPanel.presets.balanced')}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => applyModelPreset(moduleId, 'precise')}
                    >
                        {t('workflow:modelPanel.presets.precise')}
                    </Button>
                </div>
            </Form.Group>

            {moduleType === 'summary' && (
                <>
                    <Form.Group className="mb-3">
                        <LabelWithTooltip tooltip={t('workflow:tooltips.temperature')}>
                            {t('workflow:modelPanel.temperature', { val: (moduleProps.temperature?.toFixed(2) || '0.50') })}
                        </LabelWithTooltip>
                        <Form.Range
                            min={0}
                            max={1}
                            step={0.01}
                            value={moduleProps.temperature || 0.5}
                            onChange={(e) => updateProperty(moduleId, 'temperature', parseFloat(e.target.value))}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <LabelWithTooltip tooltip={t('workflow:tooltips.topP')}>
                            {t('workflow:modelPanel.topP', { val: (moduleProps.top_p?.toFixed(2) || '0.85') })}
                        </LabelWithTooltip>
                        <Form.Range
                            min={0}
                            max={1}
                            step={0.01}
                            value={moduleProps.top_p || 0.85}
                            onChange={(e) => updateProperty(moduleId, 'top_p', parseFloat(e.target.value))}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <LabelWithTooltip tooltip={t('workflow:tooltips.presencePenalty')}>
                            {t('workflow:modelPanel.presencePenalty', { val: (moduleProps.presence_penalty?.toFixed(1) || '0.0') })}
                        </LabelWithTooltip>
                        <Form.Range
                            min={-2}
                            max={2}
                            step={0.1}
                            value={moduleProps.presence_penalty || 0}
                            onChange={(e) => updateProperty(moduleId, 'presence_penalty', parseFloat(e.target.value))}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <LabelWithTooltip tooltip={t('workflow:tooltips.frequencyPenalty')}>
                            {t('workflow:modelPanel.frequencyPenalty', { val: (moduleProps.frequency_penalty?.toFixed(1) || '0.0') })}
                        </LabelWithTooltip>
                        <Form.Range
                            min={-2}
                            max={2}
                            step={0.1}
                            value={moduleProps.frequency_penalty || 0}
                            onChange={(e) => updateProperty(moduleId, 'frequency_penalty', parseFloat(e.target.value))}
                        />
                    </Form.Group>
                </>
            )}

            <Form.Group className="mb-3">
                <LabelWithTooltip tooltip={t('workflow:tooltips.customPrompt')}>
                    {t('workflow:modelPanel.customPrompt')}
                </LabelWithTooltip>
                <div className="d-flex gap-2 mb-2">
                    <Button
                        variant="primary"
                        onClick={() => handleOpenPromptModal(moduleType)}
                    >
                        {t('workflow:modelPanel.editPrompt')}
                    </Button>
                    <div className="align-self-center text-muted" style={{ fontSize: '0.9rem' }}>
                        {t('prompts:selected')}: <strong>{moduleProps.selectedPromptInfo?.name || t('prompts:defaultTemplateName', { module: moduleType })}</strong>
                    </div>
                </div>
            </Form.Group>
        </>
    );
};

// Context menu component for right-click actions
const ContextMenu = ({ x, y, onClose, menuItems }) => {
    return (
        <div
            className="context-menu"
            style={{
                position: 'absolute',
                top: `${y}px`,
                left: `${x}px`,
                zIndex: 1100,
                backgroundColor: 'white',
                boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                borderRadius: '4px',
                padding: '4px 0'
            }}
        >
            {menuItems.map((item, index) => (
                <div
                    key={index}
                    className="context-menu-item"
                    onClick={() => {
                        item.action();
                        onClose();
                    }}
                    style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                    {item.icon && <span>{item.icon}</span>}
                    {item.label}
                </div>
            ))}
        </div>
    );
};

//------------------------------------------------------
// MAIN WORKFLOW COMPONENT
//------------------------------------------------------

const Workflow = () => {
    const navigate = useNavigate();
    const { t } = useI18n();
    const notification = useNotification();
    const workflowExecutionServiceRef = useRef(null);
    const reactFlowWrapper = useRef(null);

    //------------------------------------------------------
    // STATE DECLARATIONS
    //------------------------------------------------------
    const [modules, setModules] = useState([]);        // list of module nodes added (with {id, type, name, icon})
    const [properties, setProperties] = useState({});  // mapping moduleId -> properties object
    // Shared Claude credit used up? New translation/review modules then default to OpenAI.
    const claudeUsedUpRef = useRef(false);
    useEffect(() => {
        let cancelled = false;
        const refresh = () => workflowAPI.getClaudeBudget()
            .then((resp) => { if (!cancelled && resp && resp.success) claudeUsedUpRef.current = Boolean(resp.data.exhausted); })
            .catch(() => {});
        refresh();
        const timer = setInterval(refresh, 5 * 60 * 1000);
        return () => { cancelled = true; clearInterval(timer); };
    }, []);
    const [selectedModuleId, setSelectedModuleId] = useState(null);
    const [nodes, setNodes] = useState([]);            // nodes for ReactFlow
    const [edges, setEdges] = useState([]);            // edges for ReactFlow
    const [showPromptModal, setShowPromptModal] = useState(false);
    const [promptModalContent, setPromptModalContent] = useState({
        title: '',
        content: ''
    });
    // Context menu state
    const [contextMenu, setContextMenu] = useState(null);
    // Enhanced status state
    const [jobId, setJobId] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [jobProgress, setJobProgress] = useState(0);
    const [jobDescription, setJobDescription] = useState('');
    const [jobError, setJobError] = useState(null);
    const [isPolling, setIsPolling] = useState(false);
    const [showTestResult, setShowTestResult] = useState(false);
    const [testResultData, setTestResultData] = useState(null);
    // CloudSpace/Prompt manager
    const [showCloudSpace, setShowCloudSpace] = useState(false);
    const [cloudSpaceModuleTarget, setCloudSpaceModuleTarget] = useState(null);
    const [cloudSpaceMode, setCloudSpaceMode] = useState(null); // 'termBase' or 'memoryBase'
    const [showPromptManager, setShowPromptManager] = useState(false);
    const [promptManagerModuleType, setPromptManagerModuleType] = useState(null);
    // User Profile Modal
    const [showUserProfile, setShowUserProfile] = useState(false);
    // Task Manager Modal
    const [showTaskManager, setShowTaskManager] = useState(false);
    const localFileInputRef = useRef(null);

    // Version check hook - auto-checks for updates on mount
    const {
        versionData,
        showModal: showVersionModal,
        closeModal: closeVersionModal,
        handleAcknowledge
    } = useVersionCheck(true, 1000);

    // Ensure module has default prompt from backend if missing
    const ensureDefaultPrompt = useCallback(async (moduleId, moduleType) => {
        try {
            if (!moduleId || !['translation', 'review', 'summary'].includes(moduleType)) return;
            const current = (properties[moduleId] && properties[moduleId].prompt) || '';
            if (current && current.trim() !== '') return;

            const res = await promptsAPI.getDefaultTemplate(moduleType);
            if (res && res.success && res.template && res.template.prompt_text) {
                setProperties(prev => ({
                    ...prev,
                    [moduleId]: {
                        ...(prev[moduleId] || {}),
                        prompt: res.template.prompt_text,
                        selectedPromptInfo: {
                            id: `default-${moduleType}`,
                            name: res.template.name || t('prompts:defaultTemplateName', { module: moduleType }),
                            isOrg: false,
                            isDefault: true,
                            module_type: moduleType
                        }
                    }
                }));
            }
        } catch (e) {
            console.warn('Failed to load default prompt for', moduleType, e);
        }
    }, [properties, setProperties]);

    // Before run, fill missing prompts for all prompt-based modules
    const fillMissingPrompts = useCallback(async () => {
        const tasks = [];
        modules.forEach(m => {
            if (['translation', 'review', 'summary'].includes(m.type)) {
                tasks.push(ensureDefaultPrompt(m.id, m.type));
            }
        });
        await Promise.allSettled(tasks);
    }, [modules, ensureDefaultPrompt]);

    //------------------------------------------------------
    // RESIZABLE PANELS HOOK
    //------------------------------------------------------
    const {
        sizes,
        isDragging,
        dragIndex,
        containerRef,
        handleMouseDown,
        resetSizes
    } = useResizablePanels([20, 20, 60]); // 1:1:2 ratio (20%:20%:60%)

    //------------------------------------------------------
    // MODULE MANAGEMENT FUNCTIONS
    //------------------------------------------------------

    // Get the pipeline ID for a given module ID
    const getPipelineId = (moduleId) => {
        // Find all paths reaching this module starting from fileInput nodes
        const findRootFileInput = (id, visited = new Set()) => {
            if (visited.has(id)) return null;
            visited.add(id);

            const mod = modules.find(m => m.id === id);
            if (!mod) return null;

            // If it's a File Input node, return its ID as the pipeline ID
            if (mod.type === 'fileInput') return id;

            // Find all edges pointing to this node
            const incomingEdges = edges.filter(e => e.target === id);
            if (incomingEdges.length === 0) return null;

            // Recursively find the root node for each source
            for (const edge of incomingEdges) {
                const rootId = findRootFileInput(edge.source, new Set(visited));
                if (rootId) return rootId;
            }

            return null;
        };

        return findRootFileInput(moduleId);
    };

    // Count modules of a specific type within a pipeline
    const countModulesInPipeline = (pipelineId, moduleType) => {
        if (!pipelineId) return 0;

        // Find all module IDs belonging to the same pipeline
        const modulesInPipeline = new Set();

        const traverseDownstream = (nodeId, visited = new Set()) => {
            if (visited.has(nodeId)) return;
            visited.add(nodeId);
            modulesInPipeline.add(nodeId);

            // Find all edges starting from this node
            const outgoingEdges = edges.filter(e => e.source === nodeId);
            for (const edge of outgoingEdges) {
                traverseDownstream(edge.target, visited);
            }
        };

        // Start traversal from the pipeline root node
        traverseDownstream(pipelineId);

        // Count modules of the specified type
        return Array.from(modulesInPipeline)
            .filter(id => {
                const mod = modules.find(m => m.id === id);
                return mod && mod.type === moduleType;
            }).length;
    };

    // Check if a module type can be added (prerequisites present and within pipeline limits)
    const isModuleAvailable = (moduleTypeObj) => {
        // Check if any modules have been added
        const anyModulesAdded = modules.length > 0;

        // If no modules added and trying to add something other than File Input, disallow
        if (!anyModulesAdded && moduleTypeObj.id !== 'fileInput') {
            return false;
        }

        // Check if any module is selected
        if (selectedModuleId === null && anyModulesAdded) {
            // Only allow adding File Input when no module is selected or no modules added
            return moduleTypeObj.id === 'fileInput';
        }

        // If a module is selected, check if it meets the prerequisites for this module type
        if (selectedModuleId) {
            const selectedModule = modules.find(m => m.id === selectedModuleId);
            if (!selectedModule) return false;

            // Get the pipeline that the selected module belongs to
            const pipelineId = getPipelineId(selectedModuleId);

            // Check if the pipeline already has the maximum number of this module type
            if (pipelineId) {
                const typeCount = countModulesInPipeline(pipelineId, moduleTypeObj.id);
                if (typeCount >= moduleTypeObj.maxPerPipeline) {
                    return false;
                }
            }

            // Check prerequisites - if not a file input module and no fileInput exists, unavailable
            if (moduleTypeObj.requires.length > 0 && !modules.some(m => moduleTypeObj.requires.includes(m.type))) {
                return false;
            }
        }

        return true;
    };

    // Add a module node to workflow
    const addModule = async (moduleTypeObj) => {
        if (!isModuleAvailable(moduleTypeObj)) return;

        // Create a unique id for this module instance
        const instanceId = moduleTypeObj.id + '-' + Math.random().toString(36).substr(2, 5);
        const newModule = {
            id: instanceId,
            type: moduleTypeObj.id,
            name: moduleTypeObj.name,
            icon: moduleTypeObj.icon
        };

        // Get default properties and add display name
        const newProps = getDefaultProperties(moduleTypeObj.id, claudeUsedUpRef.current);
        // Localize default display name
        if (!newProps.displayName) {
            newProps.displayName = t(`workflow:modules.${moduleTypeObj.id}.name`);
        }

        // Add to state
        setModules(prev => [...prev, newModule]);
        setProperties(prev => ({ ...prev, [instanceId]: newProps }));

        // Create ReactFlow node
        const newNode = {
            id: instanceId,
            type: 'default',
            position: { x: 250, y: 100 + modules.length * 80 },
            data: {
                label: (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px',
                        minHeight: '40px', // Ensure sufficient height
                        width: '100%'      // Use all available width
                    }}>
                        <div style={{ marginRight: '8px' }}>{moduleTypeObj.icon}</div>
                        <div style={{ fontWeight: 500 }}>{newProps.displayName}</div>
                    </div>
                )
            },
            style: {
                border: '1px solid #ccc',
                borderRadius: '8px',
                padding: '10px',
                width: 180,
                minHeight: 50,    // Minimum height to ensure node visibility
                cursor: 'pointer' // Pointer style to indicate clickability
            }
        };

        // Automatically create connection line
        let newEdges = [...edges];
        if (selectedModuleId) {
            const edgeId = `edge-${selectedModuleId}-${instanceId}`;
            newEdges.push({
                id: edgeId,
                source: selectedModuleId,
                target: instanceId,
                type: 'smoothstep',
                animated: false,
                style: { stroke: '#007bff' },
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: 20,
                    height: 20,
                    color: '#007bff',
                }
            });
        }

        setNodes(prevNodes => [...prevNodes, newNode]);
        setEdges(newEdges);
        setSelectedModuleId(instanceId);

        // Auto-fill default prompt for prompt-based modules if empty
        await ensureDefaultPrompt(instanceId, moduleTypeObj.id);
    };

    // Remove a module from workflow
    const removeModule = (moduleId) => {
        setModules(prev => prev.filter(m => m.id !== moduleId));
        setProperties(prev => {
            const newProps = { ...prev };
            delete newProps[moduleId];
            return newProps;
        });
        setNodes(prevNodes => prevNodes.filter(node => node.id !== moduleId));
        setEdges(prevEdges => prevEdges.filter(edge => edge.source !== moduleId && edge.target !== moduleId));
        if (selectedModuleId === moduleId) {
            setSelectedModuleId(null);
        }
    };

    // Update property of a module
    const updateProperty = (moduleId, prop, value) => {
        setProperties(prev => ({
            ...prev,
            [moduleId]: {
                ...prev[moduleId],
                [prop]: value
            }
        }));

        // If updating displayName, also update the node's display name
        if (prop === 'displayName') {
            const moduleInfo = modules.find(m => m.id === moduleId);
            if (moduleInfo) {
                setNodes(prevNodes =>
                    prevNodes.map(node => {
                        if (node.id === moduleId) {
                            return {
                                ...node,
                                data: {
                                    ...node.data,
                                    label: (
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '8px',
                                            minHeight: '40px', // Ensure sufficient height
                                            width: '100%'      // Use all available width
                                        }}>
                                            <div style={{ marginRight: '8px' }}>{moduleInfo.icon}</div>
                                            <div style={{ fontWeight: 500 }}>{value}</div>
                                        </div>
                                    )
                                }
                            };
                        }
                        return node;
                    })
                );
            }
        }
    };

    // Apply a model preset to a module
    const applyModelPreset = (moduleId, presetName) => {
        const preset = modelPresets[presetName];
        if (preset && moduleId) {
            setProperties(prev => ({
                ...prev,
                [moduleId]: {
                    ...prev[moduleId],
                    ...preset
                }
            }));
        }
    };

    //------------------------------------------------------
    // FILE HANDLING FUNCTIONS
    //------------------------------------------------------

    // Load columns from uploaded file
    const loadColumnsFromFile = (file, moduleId, moduleType) => {
        if (!file) return;

        // Check file extension
        const fileName = file.name.toLowerCase();
        if (!fileName.endsWith('.csv') && !fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
            alert('Please upload a CSV or Excel file.');
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const arrayBuffer = event.target.result;
                let columns = [];

                if (fileName.endsWith('.csv')) {
                    const decoder = new TextDecoder('utf-8');
                    const csv = decoder.decode(new Uint8Array(arrayBuffer));
                    // Simple CSV parsing for header row
                    const firstLine = csv.split('\n')[0];
                    columns = firstLine.split(',').map(col => col.trim().replace(/^"|"$/g, ''));
                } else {
                    // Excel file
                    const data = new Uint8Array(arrayBuffer);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    columns = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0];
                }

                // Update the module properties with available columns and file info
                updateProperty(moduleId, 'availableColumns', columns);
                updateProperty(moduleId, 'columns', columns); // Store for persistence
                updateProperty(moduleId, 'fileName', file.name); // Store filename for display
                updateProperty(moduleId, 'fileSize', file.size); // Store file size
                updateProperty(moduleId, 'fileType', file.type); // Store file type

                // If updating an existing module, keep previous selections if possible
                const currentProps = properties[moduleId] || {};

                // If previous sourceColumn is not in new columns, reset it
                if (currentProps.sourceColumn && !columns.includes(currentProps.sourceColumn)) {
                    updateProperty(moduleId, 'sourceColumn', '');
                }

                // Filter targetColumns to only include available columns
                if (currentProps.targetColumns && currentProps.targetColumns.length > 0) {
                    const validTargets = currentProps.targetColumns.filter(col => columns.includes(col));
                    updateProperty(moduleId, 'targetColumns', validTargets);
                }
            } catch (error) {
                console.error('Error reading file:', error);
                alert('Error reading file. Please check the file format.');
            }
        };

        reader.readAsArrayBuffer(file);
    };

    //------------------------------------------------------
    // UI EVENT HANDLERS
    //------------------------------------------------------

    // Handle prompt modal open - updated to use PromptManagerModal
    const handleOpenPromptModal = (type) => {
        setPromptManagerModuleType(type);
        setShowPromptManager(true);
    };

    // Handle changes in node selection on canvas
    const onSelectionChange = useCallback(({ nodes }) => {
        // Only set selectedModuleId when nodes are selected
        if (nodes && nodes.length > 0) {
            setSelectedModuleId(nodes[0].id);
        }
        // Note: We don't clear selection here, that's handled by onPaneClick
    }, []);

    // Handle save prompt
    const handleSavePrompt = (newPrompt, promptInfo = null) => {
        if (selectedModuleId) {
            updateProperty(selectedModuleId, 'prompt', newPrompt);
            // Save prompt selection info for persistence
            if (promptInfo) {
                updateProperty(selectedModuleId, 'selectedPromptInfo', promptInfo);
            }
        }
    };

    // Handle context menu close
    const closeContextMenu = useCallback(() => {
        setContextMenu(null);
    }, []);

    // Handle node right-click
    const onNodeContextMenu = useCallback((event, node) => {
        // Prevent default right-click menu
        event.preventDefault();

        // Set node as selected
        setSelectedModuleId(node.id);

        // Show custom context menu
        setContextMenu({
            x: event.clientX,
            y: event.clientY,
            menuItems: [
                {
                    label: t('workflow:contextMenu.selectModule'),
                    action: () => setSelectedModuleId(node.id),
                    icon: '✓'
                },
                {
                    label: t('workflow:contextMenu.deleteModule'),
                    action: () => removeModule(node.id),
                    icon: '🗑️'
                }
            ]
        });
    }, [setSelectedModuleId, removeModule]);

    // Handle edge right-click
    const onEdgeContextMenu = useCallback((event, edge) => {
        // Prevent default right-click menu
        event.preventDefault();

        // Show custom context menu
        setContextMenu({
            x: event.clientX,
            y: event.clientY,
            menuItems: [
                {
                    label: t('workflow:contextMenu.deleteConnection'),
                    action: () => setEdges(edges => edges.filter(e => e.id !== edge.id)),
                    icon: '🗑️'
                }
            ]
        });
    }, [setEdges]);

    // Handle background click, close context menu
    const onPaneClick = useCallback((event) => {
        // Close context menu
        closeContextMenu();

        // Existing logic: cancel node selection when clicking background
        if (
            event.target === event.currentTarget ||
            event.target.classList.contains('react-flow__pane') ||
            event.target.classList.contains('react-flow__background')
        ) {
            setSelectedModuleId(null);
        }
    }, [closeContextMenu]);

    // Handle node drag start
    const onNodeDragStart = useCallback((event, node) => {
        // Set the dragged node as selected
        setSelectedModuleId(node.id);
    }, []);

    // Handle node click
    const onNodeClick = useCallback((event, node) => {
        // Prevent event bubbling
        event.stopPropagation();
        // Set the clicked node as selected
        setSelectedModuleId(node.id);
    }, []);

    // Handle node drag
    const onNodeDrag = useCallback((event, node, nodes) => {
        // Update node position during drag
        setNodes((prevNodes) => {
            return prevNodes.map((n) => {
                if (n.id === node.id) {
                    return {
                        ...n,
                        position: {
                            x: node.position.x,
                            y: node.position.y
                        }
                    };
                }
                return n;
            });
        });
    }, []);

    // Handle node drag stop
    const onNodeDragStop = useCallback((event, node) => {
        // Ensure position is updated after drag ends
        setNodes((prevNodes) => {
            return prevNodes.map((n) => {
                if (n.id === node.id) {
                    return {
                        ...n,
                        position: {
                            x: node.position.x,
                            y: node.position.y
                        }
                    };
                }
                return n;
            });
        });
    }, []);

    // ReactFlow: onConnect event when user draws a new edge between nodes
    const onConnect = useCallback((params) => {
        setEdges(prev => addEdge({
            ...params,
            type: 'smoothstep',
            animated: false,
            style: { stroke: '#007bff' },
            markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 20,
                height: 20,
                color: '#007bff',
            },
        }, prev));
    }, []);

    // ReactFlow: onNodesDelete event (if user deletes node via UI, sync our state)
    const onNodesDelete = useCallback((deleted) => {
        deleted.forEach(delNode => {
            removeModule(delNode.id);
        });
    }, [removeModule]);

    // Logout handler
    const handleLogout = () => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userInfo');
        navigate('/login');
    };

    // Initialize execution service
    useEffect(() => {
        workflowExecutionServiceRef.current = new WorkflowExecutionService();
        return () => {
            workflowExecutionServiceRef.current?.stopPolling();
        };
    }, []);

    //------------------------------------------------------
    // WORKFLOW TEMPLATE MANAGEMENT
    //------------------------------------------------------

    // Load a workflow template
    const loadTemplate = async (templateId) => {
        // Clear existing
        setModules([]);
        setProperties({});
        setNodes([]);
        setEdges([]);
        setSelectedModuleId(null);

        // Define templates
        if (templateId === 'A') {
            // Template A: File Input -> Translation
            const fileMod = moduleTypes.find(m => m.id === 'fileInput');
            const transMod = moduleTypes.find(m => m.id === 'translation');
            if (!fileMod || !transMod) return;

            // Add File Input
            const fileId = fileMod.id + '-' + Math.random().toString(36).substr(2, 5);
            const fileModule = { id: fileId, type: fileMod.id, name: fileMod.name, icon: fileMod.icon };
            const fileProps = getDefaultProperties(fileMod.id);
            fileProps.displayName = t('workflow:modules.fileInput.name');

            // Add Translation
            const transId = transMod.id + '-' + Math.random().toString(36).substr(2, 5);
            const transModule = { id: transId, type: transMod.id, name: transMod.name, icon: transMod.icon };
            const transProps = getDefaultProperties(transMod.id, claudeUsedUpRef.current);
            transProps.displayName = t('workflow:modules.translation.name');

            // Set state
            const newModules = [fileModule, transModule];
            const newProps = { [fileId]: fileProps, [transId]: transProps };
            const newNodes = [
                {
                    id: fileId,
                    type: 'default',
                    position: { x: 100, y: 100 },
                    data: {
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ marginRight: '8px' }}>{fileMod.icon}</div>
                                <div>{fileProps.displayName}</div>
                            </div>
                        )
                    },
                    style: {
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '10px',
                        width: 180
                    }
                },
                {
                    id: transId,
                    type: 'default',
                    position: { x: 100, y: 250 },
                    data: {
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ marginRight: '8px' }}>{transMod.icon}</div>
                                <div>{transProps.displayName}</div>
                            </div>
                        )
                    },
                    style: {
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '10px',
                        width: 180
                    }
                }
            ];
            const newEdges = [
                {
                    id: 'edge-file-trans',
                    source: fileId,
                    target: transId,
                    type: 'smoothstep',
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        width: 20,
                        height: 20,
                        color: '#007bff',
                    }
                }
            ];

            setModules(newModules);
            setProperties(newProps);
            setNodes(newNodes);
            setEdges(newEdges);

            // Ensure default prompt for translation
            await ensureDefaultPrompt(transId, transMod.id);
        } else if (templateId === 'B') {
            // Template B: File Input -> Term Base -> Memory Base -> Translation -> Review
            const fileMod = moduleTypes.find(m => m.id === 'fileInput');
            const termMod = moduleTypes.find(m => m.id === 'termBase');
            const memMod = moduleTypes.find(m => m.id === 'memoryBase');
            const transMod = moduleTypes.find(m => m.id === 'translation');
            const reviewMod = moduleTypes.find(m => m.id === 'review');

            if (!fileMod || !termMod || !memMod || !transMod || !reviewMod) return;

            // Create modules with unique IDs
            const fileId = fileMod.id + '-' + Math.random().toString(36).substr(2, 5);
            const termId = termMod.id + '-' + Math.random().toString(36).substr(2, 5);
            const memId = memMod.id + '-' + Math.random().toString(36).substr(2, 5);
            const transId = transMod.id + '-' + Math.random().toString(36).substr(2, 5);
            const reviewId = reviewMod.id + '-' + Math.random().toString(36).substr(2, 5);

            // Create module objects
            const newModules = [
                { id: fileId, type: fileMod.id, name: fileMod.name, icon: fileMod.icon },
                { id: termId, type: termMod.id, name: termMod.name, icon: termMod.icon },
                { id: memId, type: memMod.id, name: memMod.name, icon: memMod.icon },
                { id: transId, type: transMod.id, name: transMod.name, icon: transMod.icon },
                { id: reviewId, type: reviewMod.id, name: reviewMod.name, icon: reviewMod.icon }
            ];

            // Create module properties
            const fileProps = getDefaultProperties(fileMod.id);
            fileProps.displayName = t('workflow:modules.fileInput.name');

            const termProps = getDefaultProperties(termMod.id);
            termProps.displayName = t('workflow:modules.termBase.name');

            const memProps = getDefaultProperties(memMod.id);
            memProps.displayName = t('workflow:modules.memoryBase.name');

            const transProps = getDefaultProperties(transMod.id, claudeUsedUpRef.current);
            transProps.displayName = t('workflow:modules.translation.name');

            const reviewProps = getDefaultProperties(reviewMod.id, claudeUsedUpRef.current);
            reviewProps.displayName = t('workflow:modules.review.name');

            const newProps = {
                [fileId]: fileProps,
                [termId]: termProps,
                [memId]: memProps,
                [transId]: transProps,
                [reviewId]: reviewProps
            };

            // Create nodes for ReactFlow
            const newNodes = [
                {
                    id: fileId,
                    type: 'default',
                    position: { x: 100, y: 80 },
                    data: {
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ marginRight: '8px' }}>{fileMod.icon}</div>
                                <div>{fileProps.displayName}</div>
                            </div>
                        )
                    },
                    style: {
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '10px',
                        width: 180
                    }
                },
                {
                    id: termId,
                    type: 'default',
                    position: { x: 100, y: 160 },
                    data: {
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ marginRight: '8px' }}>{termMod.icon}</div>
                                <div>{termProps.displayName}</div>
                            </div>
                        )
                    },
                    style: {
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '10px',
                        width: 180
                    }
                },
                {
                    id: memId,
                    type: 'default',
                    position: { x: 100, y: 240 },
                    data: {
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ marginRight: '8px' }}>{memMod.icon}</div>
                                <div>{memProps.displayName}</div>
                            </div>
                        )
                    },
                    style: {
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '10px',
                        width: 180
                    }
                },
                {
                    id: transId,
                    type: 'default',
                    position: { x: 100, y: 320 },
                    data: {
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ marginRight: '8px' }}>{transMod.icon}</div>
                                <div>{transProps.displayName}</div>
                            </div>
                        )
                    },
                    style: {
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '10px',
                        width: 180
                    }
                },
                {
                    id: reviewId,
                    type: 'default',
                    position: { x: 100, y: 400 },
                    data: {
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ marginRight: '8px' }}>{reviewMod.icon}</div>
                                <div>{reviewProps.displayName}</div>
                            </div>
                        )
                    },
                    style: {
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '10px',
                        width: 180
                    }
                }
            ];

            // Create edges with arrows
            const newEdges = [
                {
                    id: 'edge-file-term',
                    source: fileId,
                    target: termId,
                    type: 'smoothstep',
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        width: 20,
                        height: 20,
                        color: '#007bff',
                    }
                },
                {
                    id: 'edge-term-mem',
                    source: termId,
                    target: memId,
                    type: 'smoothstep',
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        width: 20,
                        height: 20,
                        color: '#007bff',
                    }
                },
                {
                    id: 'edge-mem-trans',
                    source: memId,
                    target: transId,
                    type: 'smoothstep',
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        width: 20,
                        height: 20,
                        color: '#007bff',
                    }
                },
                {
                    id: 'edge-trans-review',
                    source: transId,
                    target: reviewId,
                    type: 'smoothstep',
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        width: 20,
                        height: 20,
                        color: '#007bff',
                    }
                }
            ];

            setModules(newModules);
            setProperties(newProps);
            setNodes(newNodes);
            setEdges(newEdges);

            // Ensure default prompts for translation and review
            await ensureDefaultPrompt(transId, transMod.id);
            await ensureDefaultPrompt(reviewId, reviewMod.id);
        } else if (templateId === 'C') {
            // Template C: File Input -> Term Base -> Review
            const fileMod = moduleTypes.find(m => m.id === 'fileInput');
            const termMod = moduleTypes.find(m => m.id === 'termBase');
            const reviewMod = moduleTypes.find(m => m.id === 'review');

            if (!fileMod || !termMod || !reviewMod) return;

            // Create unique IDs for modules
            const fileId = fileMod.id + '-' + Math.random().toString(36).substr(2, 5);
            const termId = termMod.id + '-' + Math.random().toString(36).substr(2, 5);
            const reviewId = reviewMod.id + '-' + Math.random().toString(36).substr(2, 5);

            // Create module objects
            const newModules = [
                { id: fileId, type: fileMod.id, name: fileMod.name, icon: fileMod.icon },
                { id: termId, type: termMod.id, name: termMod.name, icon: termMod.icon },
                { id: reviewId, type: reviewMod.id, name: reviewMod.name, icon: reviewMod.icon }
            ];

            // Create module properties
            const fileProps = getDefaultProperties(fileMod.id);
            fileProps.displayName = t('workflow:modules.fileInput.name');

            const termProps = getDefaultProperties(termMod.id);
            termProps.displayName = t('workflow:modules.termBase.name');

            const reviewProps = getDefaultProperties(reviewMod.id, claudeUsedUpRef.current);
            reviewProps.displayName = t('workflow:modules.review.name');

            const newProps = {
                [fileId]: fileProps,
                [termId]: termProps,
                [reviewId]: reviewProps
            };

            // Create nodes for ReactFlow
            const newNodes = [
                {
                    id: fileId,
                    type: 'default',
                    position: { x: 100, y: 80 },
                    data: {
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ marginRight: '8px' }}>{fileMod.icon}</div>
                                <div>{fileProps.displayName}</div>
                            </div>
                        )
                    },
                    style: { border: '1px solid #ccc', borderRadius: '8px', padding: '10px', width: 180 }
                },
                {
                    id: termId,
                    type: 'default',
                    position: { x: 100, y: 160 },
                    data: {
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ marginRight: '8px' }}>{termMod.icon}</div>
                                <div>{termProps.displayName}</div>
                            </div>
                        )
                    },
                    style: { border: '1px solid #ccc', borderRadius: '8px', padding: '10px', width: 180 }
                },
                {
                    id: reviewId,
                    type: 'default',
                    position: { x: 100, y: 240 },
                    data: {
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ marginRight: '8px' }}>{reviewMod.icon}</div>
                                <div>{reviewProps.displayName}</div>
                            </div>
                        )
                    },
                    style: { border: '1px solid #ccc', borderRadius: '8px', padding: '10px', width: 180 }
                }
            ];

            // Create edges with arrows
            const newEdges = [
                {
                    id: 'edge-file-term',
                    source: fileId,
                    target: termId,
                    type: 'smoothstep',
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        width: 20,
                        height: 20,
                        color: '#007bff',
                    }
                },
                {
                    id: 'edge-term-review',
                    source: termId,
                    target: reviewId,
                    type: 'smoothstep',
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        width: 20,
                        height: 20,
                        color: '#007bff',
                    }
                }
            ];

            setModules(newModules);
            setProperties(newProps);
            setNodes(newNodes);
            setEdges(newEdges);

            // Ensure default prompt for review
            await ensureDefaultPrompt(reviewId, reviewMod.id);
        }
    };

    //------------------------------------------------------
    // WORKFLOW EXECUTION FUNCTIONS
    //------------------------------------------------------

    // Save workflow configuration (send to backend)
    const handleSaveWorkflow = async () => {
        // Construct payload for saving
        const payload = {
            name: `Workflow ${new Date().toISOString().slice(0, 16).replace('T', ' ')}`,  // you might allow user to name it
            description: '',
            modules: modules.map(m => ({ id: m.id, type: m.type })),  // store module type and unique id
            properties: properties,
            edges: edges.map(e => ({ source: e.source, target: e.target }))
        };
        try {
            const res = await fetch('/api/workflow/save/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success) {
                alert('Workflow saved successfully!');
            } else {
                alert('Save failed: ' + data.message);
            }
        } catch (err) {
            console.error('Error saving workflow:', err);
        }
    };

    // Helpers for validation
    const getModuleById = (id) => modules.find(m => m.id === id);
    const getProps = (id) => properties[id] || {};
    const hasUpstreamTranslation = (nodeId) => {
        const visited = new Set();
        const stack = [nodeId];
        while (stack.length) {
            const curr = stack.pop();
            if (visited.has(curr)) continue;
            visited.add(curr);
            const incoming = edges.filter(e => e.target === curr).map(e => e.source);
            for (const src of incoming) {
                const mod = getModuleById(src);
                if (mod && mod.type === 'translation') return true;
                stack.push(src);
            }
        }
        return false;
    };
    const validateFileInputProps = (fileId) => {
        const p = getProps(fileId);
        const errs = [];
        const hasSourceFile = (p.fileSource === 'cloud' && p.cloudFile) || (p.fileSource !== 'cloud' && p.file);
        if (!hasSourceFile) errs.push('missingSourceFile');
        if (!p.sourceLang) errs.push('missingSourceLang');
        if (!p.targetLangs || p.targetLangs.length === 0) errs.push('missingTargetLangs');
        if (!p.sourceColumn) errs.push('missingSourceColumn');
        if (p.fileMode === 'calibration') {
            const mappings = p.targetLangs || [];
            const ok = mappings.every(l => p.targetColumnMappings && p.targetColumnMappings[l]);
            if (!ok) errs.push('missingTargetMappings');
        }
        return errs;
    };

    // Execute (run) the workflow with new WorkflowExecutionService
    const handleRunWorkflow = async () => {
        try {
            // Pre-check: user cost vs. limit
            try {
                const profileResp = await userAPI.getUserProfile();
                if (profileResp && profileResp.success && profileResp.data && profileResp.data.user_info) {
                    const info = profileResp.data.user_info;
                    const current = Number(info.current_cost || 0);
                    const limit = Number(info.cost_limit || 0);
                    if (limit > 0 && current >= limit) {
                        notification.showWarning(t('workflow:alerts.costLimitExceeded'));
                        return;
                    }
                }
            } catch (e) {
                console.warn('Cost check skipped due to profile fetch error:', e);
            }

            // Pre-check: shared monthly Claude credit
            const usesClaude = modules.some(m => String(getProps(m.id).model || '').toLowerCase().startsWith('claude'));
            if (usesClaude) {
                try {
                    const budgetResp = await workflowAPI.getClaudeBudget();
                    if (budgetResp && budgetResp.success) claudeUsedUpRef.current = Boolean(budgetResp.data.exhausted);
                    if (budgetResp && budgetResp.success && budgetResp.data.exhausted) {
                        notification.showWarning(t('workflow:alerts.claudeBudgetUsedUp', { date: String(budgetResp.data.resets_at).slice(0, 10) }));
                        return;
                    }
                } catch (e) {
                    console.warn('Claude budget check skipped:', e);
                }
            }

            // a) require at least one fileInput
            const fileInputs = modules.filter(m => m.type === 'fileInput');
            if (fileInputs.length === 0) {
                notification.showWarning(t('workflow:validation.addFileInput'));
                return;
            }

            // b) validate required file input settings
            for (const fi of fileInputs) {
                const errs = validateFileInputProps(fi.id);
                if (errs.length > 0) {
                    const messages = errs.map(e => t(`workflow:validation.${e}`)).join('\n');
                    notification.showWarning(messages);
                    return;
                }
            }

            // c) If any review has no upstream translation in its pipeline, require file mode to be calibration for that pipeline's fileInput
            const reviews = modules.filter(m => m.type === 'review');
            for (const rv of reviews) {
                if (!hasUpstreamTranslation(rv.id)) {
                    // find root file input upstream
                    const upstream = [];
                    const visited = new Set();
                    const stack = [rv.id];
                    while (stack.length) {
                        const curr = stack.pop();
                        if (visited.has(curr)) continue;
                        visited.add(curr);
                        const incoming = edges.filter(e => e.target === curr).map(e => e.source);
                        for (const src of incoming) {
                            upstream.push(src);
                            stack.push(src);
                        }
                    }
                    const rootFile = upstream.map(getModuleById).find(m => m && m.type === 'fileInput');
                    if (rootFile) {
                        const p = getProps(rootFile.id);
                        if (p.fileMode !== 'calibration') {
                            notification.showWarning(t('workflow:validation.reviewNeedsCalibration'));
                            return;
                        }
                    }
                }
            }

            // Ensure prompt-based modules have a prompt (no-empty guarantee)
            await fillMissingPrompts();

            // Build workflow data structure compatible with new backend
            const workflowData = {
                modules: modules.map(m => ({ id: m.id, type: m.type, inputs: m.inputs || [] })),
                properties: properties
            };

            // Determine selected row if any (legacy UI不提供test mode，这里始终 false）
            const service = workflowExecutionServiceRef.current;
            const execResult = await service.executeWorkflow({ modules: workflowData.modules, properties: workflowData.properties }, false, null);

            if (execResult.success) {
                setJobId(execResult.jobId);
                setJobStatus('PENDING');
                setJobDescription(t('workflow:messages.started'));
                setJobError(null);
                setIsPolling(true);
                notification.showSuccess(t('workflow:messages.started'), { requestId: execResult.requestId });

                // Start polling
                service.startPolling(execResult.jobId, (statusUpdate) => {
                    const { status, progress, description, error, result, requestId } = statusUpdate;
                    setJobStatus(status);
                    setJobProgress(progress || 0);
                    setJobDescription(description || '');
                    if (error) setJobError(error);

                    // When finished, show results if present
                    if (status === 'SUCCESS') {
                        setIsPolling(false);
                        // Result may include result_files/download_urls or inline results
                        setTestResultData(result || null);
                        setShowTestResult(!!result);
                        notification.showSuccess(t('workflow:messages.completed'), { requestId });
                    } else if (status === 'FAILED') {
                        setIsPolling(false);
                        notification.showError(error || t('workflow:messages.failed'), { requestId });
                    }
                });
            } else {
                notification.showError(execResult.message || t('workflow:messages.execFailed'));
            }
        } catch (error) {
            console.error('Workflow execution error:', error);
            notification.showError(error.message || t('workflow:messages.execFailed'), { requestId: error.requestId });
        }
    };

    //------------------------------------------------------
    // EFFECTS
    //------------------------------------------------------

    // Update node style when selected
    useEffect(() => {
        setNodes(prevNodes =>
            prevNodes.map(node => ({
                ...node,
                style: {
                    ...node.style,
                    border: node.id === selectedModuleId
                        ? '3px solid #007bff'
                        : '1px solid #ccc',
                    boxShadow: node.id === selectedModuleId
                        ? '0 0 10px rgba(0, 123, 255, 0.5)'
                        : 'none'
                }
            }))
        );
    }, [selectedModuleId]);

    // Add document-level click event to close context menu
    useEffect(() => {
        const handleClickOutside = () => closeContextMenu();
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [closeContextMenu]);

    //------------------------------------------------------
    // RENDERING
    //------------------------------------------------------

    // Render the property panel for the selected module
    const renderPropertiesPanel = () => {
        if (!selectedModuleId) {
            return (
                <div className="properties-panel"
                    style={{ padding: '1.5rem', background: '#fff', borderRadius: 8, boxShadow: 'var(--shadow)' }}>
                    <h5>{t('workflow:properties.title')}</h5>
                    <p className="text-muted" style={{ marginTop: '4rem', textAlign: 'center' }}>
                        {t('workflow:properties.emptyHint')}
                    </p>
                </div>
            );
        }
        const module = modules.find(m => m.id === selectedModuleId);
        if (!module) return null;
        const moduleType = module.type;
        const moduleProps = properties[selectedModuleId] || {};
        return (
            <div className="properties-panel" style={{
                padding: '1.5rem',
                background: '#fff',
                borderRadius: 8,
                boxShadow: 'var(--shadow)',
                maxHeight: 'calc(100vh - 130px)',
                overflowY: 'auto'
            }}>
                <h5>{t('workflow:properties.moduleTitle', { name: t(`workflow:modules.${module.type}.name`) })}</h5>
                <Form style={{ marginTop: '1rem' }}>
                    {moduleType === 'fileInput' && (
                        <>
                            {/* Display Name - Single row at the top */}
                            <Form.Group className="mb-3">
                                <LabelWithTooltip tooltip={t('workflow:tooltips.displayName')}>
                                    {t('workflow:modelPanel.displayName')}
                                </LabelWithTooltip>
                                <Form.Control
                                    type="text"
                                    value={moduleProps.displayName || ''}
                                    onChange={(e) => updateProperty(selectedModuleId, 'displayName', e.target.value)}
                                    placeholder={t('workflow:modelPanel.displayName')}
                                />
                            </Form.Group>

                            {/* File Mode and Text Type row */}
                            <Row className="g-3 mb-3">
                                <Col md={6}>
                                    <LabelWithTooltip tooltip={t('workflow:tooltips.fileMode')}>
                                        {t('workflow:fileInput.fileMode')}
                                    </LabelWithTooltip>
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant={moduleProps.fileMode !== 'calibration' ? 'primary' : 'outline-primary'}
                                            size="sm"
                                            onClick={() => {
                                                updateProperty(selectedModuleId, 'fileMode', 'translation');
                                                updateProperty(selectedModuleId, 'targetColumnMappings', {});
                                            }}
                                            className="flex-fill"
                                        >
                                            <FaLanguage className="me-2" />
                                            {t('workflow:fileInput.mode.toTranslate')}
                                        </Button>
                                        <Button
                                            variant={moduleProps.fileMode === 'calibration' ? 'success' : 'outline-success'}
                                            size="sm"
                                            onClick={() => {
                                                updateProperty(selectedModuleId, 'fileMode', 'calibration');
                                                if (!moduleProps.targetColumnMappings) {
                                                    updateProperty(selectedModuleId, 'targetColumnMappings', {});
                                                }
                                            }}
                                            className="flex-fill"
                                        >
                                            <FaCheckCircle className="me-2" />
                                            {t('workflow:fileInput.mode.calibration')}
                                        </Button>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <LabelWithTooltip tooltip={t('workflow:tooltips.textType')}>
                                        {t('workflow:fileInput.textType.label')}
                                    </LabelWithTooltip>
                                    <Form.Select
                                        value={moduleProps.textType || 'dialogue'}
                                        onChange={(e) => updateProperty(selectedModuleId, 'textType', e.target.value)}
                                    >
                                        <option value="dialogue">{t('workflow:fileInput.textType.options.dialogue')}</option>
                                        <option value="system">{t('workflow:fileInput.textType.options.system')}</option>
                                    </Form.Select>
                                </Col>
                            </Row>

                            {/* File Source Selection */}
                            <div className="settings-section">
                                <LabelWithTooltip tooltip={t('workflow:tooltips.fileSource')} className="mb-2">
                                    {t('workflow:fileInput.fileSource')}
                                </LabelWithTooltip>
                                <div className="d-flex gap-2 mb-2 file-source-toggle">
                                    <Button
                                        variant={moduleProps.fileSource !== 'cloud' ? 'primary' : 'outline-primary'}
                                        size="sm"
                                        onClick={() => {
                                            updateProperty(selectedModuleId, 'fileSource', 'local');
                                            updateProperty(selectedModuleId, 'cloudFile', null);
                                            updateProperty(selectedModuleId, 'columns', []);
                                        }}
                                        className="flex-fill"
                                    >
                                        <FaFile className="me-2" />
                                        {t('workflow:fileInput.localUpload')}
                                    </Button>
                                    <Button
                                        variant={moduleProps.fileSource === 'cloud' ? 'success' : 'outline-success'}
                                        size="sm"
                                        onClick={() => {
                                            updateProperty(selectedModuleId, 'fileSource', 'cloud');
                                            updateProperty(selectedModuleId, 'file', null);
                                            updateProperty(selectedModuleId, 'columns', []);
                                        }}
                                        className="flex-fill"
                                    >
                                        <FaDatabase className="me-2" />
                                        {t('workflow:fileInput.cloudFile')}
                                    </Button>
                                </div>
                            </div>

                            <Form.Group className="mb-3 settings-section">
                                <LabelWithTooltip
                                    tooltip={t('workflow:tooltips.sourceFile')}
                                    required
                                    className="mb-2"
                                >
                                    {t('workflow:fileInput.sourceFile')}
                                </LabelWithTooltip>
                                {moduleProps.fileSource === 'cloud' ? (
                                    <>
                                        <InputGroup className="file-input-row">
                                            <Form.Control
                                                type="text"
                                                readOnly
                                                value={moduleProps.cloudFile ? moduleProps.cloudFile.name : ''}
                                                placeholder={t('workflow:fileInput.noCloudSelected')}
                                            />
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => {
                                                    setCloudSpaceMode('fileInput');
                                                    setCloudSpaceModuleTarget(selectedModuleId);
                                                    setShowCloudSpace(true);
                                                }}
                                            >
                                                {t('workflow:fileInput.selectFile')}
                                            </Button>
                                            {moduleProps.cloudFile && (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => {
                                                        updateProperty(selectedModuleId, 'cloudFile', null);
                                                        updateProperty(selectedModuleId, 'columns', []);
                                                        updateProperty(selectedModuleId, 'availableColumns', []);
                                                        updateProperty(selectedModuleId, 'sourceColumn', '');
                                                    }}
                                                >
                                                    {t('common:actions.remove')}
                                                </Button>
                                            )}
                                        </InputGroup>
                                        <Form.Text className="text-muted">{t('workflow:fileInput.cloudHint')}</Form.Text>
                                    </>
                                ) : (
                                    <>
                                        <input
                                            ref={localFileInputRef}
                                            type="file"
                                            accept=".csv,.xlsx,.xls"
                                            className="d-none"
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                    updateProperty(selectedModuleId, 'file', file);
                                                    loadColumnsFromFile(file, selectedModuleId, 'fileInput');
                                                }
                                            }}
                                        />
                                        <InputGroup className="file-input-row">
                                            <Form.Control
                                                type="text"
                                                readOnly
                                                value={moduleProps.fileName ? `${moduleProps.fileName} (${(moduleProps.fileSize / 1024).toFixed(1)} KB)` : ''}
                                                placeholder={t('workflow:fileInput.noLocalSelected')}
                                            />
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => localFileInputRef.current && localFileInputRef.current.click()}
                                            >
                                                {t('workflow:fileInput.selectFile')}
                                            </Button>
                                            {moduleProps.fileName && (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => {
                                                        updateProperty(selectedModuleId, 'file', null);
                                                        updateProperty(selectedModuleId, 'fileName', '');
                                                        updateProperty(selectedModuleId, 'fileSize', 0);
                                                        updateProperty(selectedModuleId, 'fileType', '');
                                                        updateProperty(selectedModuleId, 'columns', []);
                                                        updateProperty(selectedModuleId, 'availableColumns', []);
                                                        updateProperty(selectedModuleId, 'sourceColumn', '');
                                                        if (localFileInputRef.current) localFileInputRef.current.value = null;
                                                    }}
                                                >
                                                    {t('common:actions.remove')}
                                                </Button>
                                            )}
                                        </InputGroup>
                                    </>
                                )}
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <LabelWithTooltip
                                    tooltip={t('workflow:tooltips.sourceLang')}
                                    required
                                >
                                    {t('workflow:fileInput.sourceLangLabel')}
                                </LabelWithTooltip>
                                <Form.Select
                                    value={moduleProps.sourceLang || ''}
                                    onChange={(e) => updateProperty(selectedModuleId, 'sourceLang', e.target.value)}
                                >
                                    <option value="">{t('workflow:fileInput.selectSourceLang')}</option>
                                    {languageOptions.map((lang) => (
                                        <option key={lang.value} value={lang.value}>{lang.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            {((moduleProps.availableColumns && moduleProps.availableColumns.length > 0) ||
                                (moduleProps.columns && moduleProps.columns.length > 0) ||
                                (moduleProps.cloudFile)) && (
                                    <>
                                        <Form.Group className="mb-3">
                                            <LabelWithTooltip
                                                tooltip={t('workflow:tooltips.sourceColumn')}
                                                required
                                            >
                                                {t('workflow:cloud.sourceColumnLabel')}
                                            </LabelWithTooltip>
                                            <Form.Select
                                                value={moduleProps.sourceColumn || ''}
                                                onChange={(e) => updateProperty(selectedModuleId, 'sourceColumn', e.target.value)}
                                            >
                                                <option value="">{t('workflow:cloud.selectSourceOption')}</option>
                                                {getAvailableColumns(moduleProps).map((col, idx) => (
                                                    <option key={idx} value={col}>{col}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </>
                                )}

                            {/* Target Languages moved below Source Column (always visible) */}
                            <Form.Group className="mb-3">
                                <LabelWithTooltip
                                    tooltip={t('workflow:tooltips.targetLangs')}
                                    required
                                >
                                    {t('workflow:fileInput.targetLangsLabel')}
                                </LabelWithTooltip>
                                <Select
                                    isMulti
                                    options={languageOptions}
                                    value={(moduleProps.targetLangs || []).map(lang =>
                                        languageOptions.find(option => option.value === lang) || {
                                            value: lang,
                                            label: lang
                                        }
                                    )}
                                    menuPortalTarget={document.body}
                                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    onChange={(selected) => {
                                        const langs = selected ? selected.map(item => item.value) : [];
                                        updateProperty(selectedModuleId, 'targetLangs', langs);
                                    }}
                                />
                            </Form.Group>

                            {/* Target Column Mappings for Calibration Mode (below Target Languages) */}
                            {((moduleProps.availableColumns && moduleProps.availableColumns.length > 0) ||
                                (moduleProps.columns && moduleProps.columns.length > 0) ||
                                (moduleProps.cloudFile)) && (
                                    moduleProps.fileMode === 'calibration' && moduleProps.targetLangs && moduleProps.targetLangs.length > 0 && (
                                        <div className="mb-3">
                                            <LabelWithTooltip
                                                tooltip={t('workflow:tooltips.targetMappings')}
                                                required={moduleProps.fileMode === 'calibration'}
                                            >
                                                {t('workflow:cloud.targetLangColumnMappings')}
                                            </LabelWithTooltip>
                                            <div className="p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                                                {moduleProps.targetLangs.map((lang) => {
                                                    const suggestedColumns = getAvailableColumns(moduleProps).filter(col =>
                                                        col.toLowerCase().includes(lang.toLowerCase()) ||
                                                        col.toLowerCase().includes(languageOptions.find(opt => opt.value === lang)?.label.toLowerCase() || '')
                                                    );
                                                    return (
                                                        <div key={lang} className="mb-2">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <Badge bg="info" style={{ minWidth: '80px' }}>
                                                                    {languageOptions.find(opt => opt.value === lang)?.label || lang}
                                                                </Badge>
                                                                <Form.Select
                                                                    size="sm"
                                                                    value={moduleProps.targetColumnMappings?.[lang] || ''}
                                                                    onChange={(e) => {
                                                                        const newMappings = { ...moduleProps.targetColumnMappings };
                                                                        newMappings[lang] = e.target.value;
                                                                        updateProperty(selectedModuleId, 'targetColumnMappings', newMappings);
                                                                    }}
                                                                    className="flex-fill"
                                                                >
                                                                    <option value="">{t('workflow:fileInput.selectColumnFor', { lang })}</option>
                                                                    {suggestedColumns.length > 0 && (
                                                                        <optgroup label={t('workflow:fileInput.suggested')}>
                                                                            {suggestedColumns.map((col, idx) => (
                                                                                <option key={`sug-${idx}`} value={col}>⭐ {col}</option>
                                                                            ))}
                                                                        </optgroup>
                                                                    )}
                                                                    <optgroup label={t('workflow:fileInput.allColumns')}>
                                                                        {getAvailableColumns(moduleProps).map((col, idx) => (
                                                                            <option key={`all-${idx}`} value={col}>{col}</option>
                                                                        ))}
                                                                    </optgroup>
                                                                </Form.Select>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="mt-2"
                                                    onClick={() => {
                                                        const autoMappings = {};
                                                        moduleProps.targetLangs.forEach(lang => {
                                                            const bestMatch = getAvailableColumns(moduleProps).find(col =>
                                                                col.toLowerCase() === lang.toLowerCase() ||
                                                                col.toLowerCase().includes(lang.toLowerCase())
                                                            );
                                                            if (bestMatch) {
                                                                autoMappings[lang] = bestMatch;
                                                            }
                                                        });
                                                        if (Object.keys(autoMappings).length > 0) {
                                                            updateProperty(selectedModuleId, 'targetColumnMappings', autoMappings);
                                                        }
                                                    }}
                                                >
                                                    <FaSearch className="me-1" /> {t('workflow:fileInput.autoDetect')}
                                                </Button>
                                            </div>
                                            <Form.Text className="text-muted">{t('workflow:fileInput.mapHint')}</Form.Text>
                                        </div>
                                    )
                                )}

                            {/* Bottom fields: Game Type and Story */}
                            <Form.Group className="mb-3">
                                <LabelWithTooltip tooltip={t('workflow:tooltips.gameType')}>
                                    {t('workflow:fileInput.gameTypeLabel')}
                                </LabelWithTooltip>
                                <Form.Control
                                    type="text"
                                    value={moduleProps.gameType || ''}
                                    onChange={(e) => updateProperty(selectedModuleId, 'gameType', e.target.value)}
                                    placeholder={t('workflow:fileInput.gameTypePlaceholder')}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <LabelWithTooltip tooltip={t('workflow:tooltips.story')}>
                                    {t('workflow:fileInput.storyLabel')}
                                </LabelWithTooltip>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={moduleProps.story || ''}
                                    onChange={(e) => updateProperty(selectedModuleId, 'story', e.target.value)}
                                    placeholder={t('workflow:fileInput.storyPlaceholder')}
                                />
                            </Form.Group>
                        </>
                    )}
                    {moduleType === 'termBase' && (
                        <>
                            <Form.Group className="mb-3">
                                <LabelWithTooltip tooltip={t('workflow:tooltips.displayName')}>
                                    {t('workflow:modelPanel.displayName')}
                                </LabelWithTooltip>
                                <Form.Control
                                    type="text"
                                    value={moduleProps.displayName || ''}
                                    onChange={(e) => updateProperty(selectedModuleId, 'displayName', e.target.value)}
                                    placeholder={t('workflow:modelPanel.displayName')}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <LabelWithTooltip tooltip={t('workflow:tooltips.termBaseFile')}>
                                    {t('workflow:modules.termBase.cloudFileLabel')}
                                </LabelWithTooltip>
                                <div className="d-grid">
                                    <Button
                                        variant={moduleProps.cloudFile ? "outline-success" : "primary"}
                                        onClick={() => {
                                            setCloudSpaceMode('termBase');
                                            setCloudSpaceModuleTarget(selectedModuleId);
                                            setShowCloudSpace(true);
                                        }}
                                    >
                                        {moduleProps.cloudFile ? `✓ ${moduleProps.cloudFile.name}` : t('workflow:cloud.selectVectorizedFromCloud')}
                                    </Button>
                                    {moduleProps.cloudFile && (
                                        <div className="mt-2 p-3 border rounded" style={{
                                            backgroundColor: '#e8f5e8',
                                            borderColor: '#28a745',
                                            borderWidth: '2px'
                                        }}>
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <div className="d-flex align-items-center">
                                                        <strong style={{ color: '#155724' }}>✓ {t('workflow:cloud.selected')}</strong>
                                                        <span className="ms-2">{moduleProps.cloudFile.name}</span>
                                                        <Badge className="ms-2" bg="success">{t('cloud:cloudSpace.labels.vectorized')}</Badge>
                                                    </div>
                                                    <small className="d-block text-success mt-1">
                                                        {t('workflow:cloud.source')}: {moduleProps.cloudFile.vectorized_source_column || t('workflow:cloud.na')},
                                                        {t('workflow:cloud.targets')}: {(moduleProps.cloudFile.vectorized_target_columns || []).join(', ') || t('workflow:cloud.na')}
                                                    </small>
                                                </div>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => {
                                                        updateProperty(selectedModuleId, 'cloudFile', null);
                                                        updateProperty(selectedModuleId, 'sourceColumn', '');
                                                        updateProperty(selectedModuleId, 'targetColumns', []);
                                                        updateProperty(selectedModuleId, 'selectedCombination', '');
                                                    }}
                                                    title={t('workflow:cloud.removeSelection')}
                                                >
                                                    ×
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <Form.Text className="text-muted">{t('workflow:alerts.onlyVectorizedFiles')}</Form.Text>
                            </Form.Group>

                            {/* Vectorization combination selector */}
                            {moduleProps.cloudFile && moduleProps.cloudFile.vectorization_combinations && (
                                <Form.Group className="mb-3">
                                    <LabelWithTooltip tooltip={t('workflow:tooltips.vectorizationCombination')}>
                                        {t('cloud:vectorizationCombination')}
                                    </LabelWithTooltip>
                                    <Form.Select
                                        value={moduleProps.selectedCombination || moduleProps.cloudFile.vectorization_combinations.default_combination || ''}
                                        onChange={(e) => {
                                            const comboId = e.target.value;
                                            const combinations = moduleProps.cloudFile.vectorization_combinations.combinations;
                                            const selectedCombo = combinations[comboId];
                                            if (selectedCombo) {
                                                updateProperty(selectedModuleId, 'selectedCombination', comboId);
                                                updateProperty(selectedModuleId, 'sourceColumn', selectedCombo.source_column);
                                                updateProperty(selectedModuleId, 'targetColumns', selectedCombo.target_columns || []);
                                            }
                                        }}
                                    >
                                        {Object.entries(moduleProps.cloudFile.vectorization_combinations.combinations || {}).map(([comboId, combo]) => (
                                            <option key={comboId} value={comboId}>
                                                {combo.source_column} → {combo.target_columns?.join(', ') || t('workflow:cloud.noTargets')}
                                                {moduleProps.cloudFile.vectorization_combinations.default_combination === comboId ? ` (${t('prompts:default')})` : ''}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            )}

                            {/* Column selection based on selected cloud file */}
                            {moduleProps.cloudFile && moduleProps.selectedCombination && (
                                <>
                                    <Form.Group className="mb-3">
                                        <LabelWithTooltip tooltip={t('workflow:tooltips.sourceTermColumn')}>
                                            {t('cloud:sourceTermColumn')}
                                        </LabelWithTooltip>
                                        <Form.Select
                                            value={moduleProps.sourceColumn || ''}
                                            onChange={(e) => updateProperty(selectedModuleId, 'sourceColumn', e.target.value)}
                                            disabled
                                        >
                                            <option value={moduleProps.sourceColumn}>{moduleProps.sourceColumn}</option>
                                        </Form.Select>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <LabelWithTooltip tooltip={t('workflow:tooltips.targetTermColumns')}>
                                            {t('cloud:targetTermColumns')}
                                        </LabelWithTooltip>
                                        <Select
                                            isMulti
                                            options={(() => {
                                                const combinations = moduleProps.cloudFile.vectorization_combinations?.combinations || {};
                                                const selectedCombo = combinations[moduleProps.selectedCombination];
                                                return (selectedCombo?.target_columns || []).map(col => ({ value: col, label: col }));
                                            })()}
                                            value={(moduleProps.targetColumns || []).map(col => ({ value: col, label: col }))}
                                            onChange={(selected) => {
                                                const cols = selected ? selected.map(item => item.value) : [];
                                                updateProperty(selectedModuleId, 'targetColumns', cols);
                                            }}
                                        />
                                    </Form.Group>
                                </>
                            )}
                        </>
                    )}
                    {moduleType === 'memoryBase' && (
                        <>
                            <Form.Group className="mb-3">
                                <LabelWithTooltip tooltip={t('workflow:tooltips.displayName')}>
                                    {t('workflow:modelPanel.displayName')}
                                </LabelWithTooltip>
                                <Form.Control
                                    type="text"
                                    value={moduleProps.displayName || ''}
                                    onChange={(e) => updateProperty(selectedModuleId, 'displayName', e.target.value)}
                                    placeholder={t('workflow:modelPanel.displayName')}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <LabelWithTooltip tooltip={t('workflow:tooltips.memoryBaseFile')}>
                                    {t('workflow:modules.memoryBase.cloudFileLabel')}
                                </LabelWithTooltip>
                                <div className="d-grid">
                                    <Button
                                        variant={moduleProps.cloudFile ? "outline-success" : "primary"}
                                        onClick={() => {
                                            setCloudSpaceMode('memoryBase');
                                            setCloudSpaceModuleTarget(selectedModuleId);
                                            setShowCloudSpace(true);
                                        }}
                                    >
                                        {moduleProps.cloudFile ? `✓ ${moduleProps.cloudFile.name}` : t('workflow:cloud.selectVectorizedFromCloud')}
                                    </Button>
                                    {moduleProps.cloudFile && (
                                        <div className="mt-2 p-3 border rounded" style={{
                                            backgroundColor: '#e8f5e8',
                                            borderColor: '#28a745',
                                            borderWidth: '2px'
                                        }}>
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <div className="d-flex align-items-center">
                                                        <strong style={{ color: '#155724' }}>✓ {t('workflow:cloud.selected')}</strong>
                                                        <span className="ms-2">{moduleProps.cloudFile.name}</span>
                                                        <Badge className="ms-2" bg="success">{t('cloud:cloudSpace.labels.vectorized')}</Badge>
                                                    </div>
                                                    <small className="d-block text-success mt-1">
                                                        {t('workflow:cloud.source')}: {moduleProps.cloudFile.vectorized_source_column || t('workflow:cloud.na')},
                                                        {t('workflow:cloud.targets')}: {(moduleProps.cloudFile.vectorized_target_columns || []).join(', ') || t('workflow:cloud.na')}
                                                    </small>
                                                </div>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => {
                                                        updateProperty(selectedModuleId, 'cloudFile', null);
                                                        updateProperty(selectedModuleId, 'sourceColumn', '');
                                                        updateProperty(selectedModuleId, 'targetColumns', []);
                                                        updateProperty(selectedModuleId, 'selectedCombination', '');
                                                    }}
                                                    title={t('workflow:cloud.removeSelection')}
                                                >
                                                    ×
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <Form.Text className="text-muted">{t('workflow:alerts.onlyVectorizedFiles')}</Form.Text>
                            </Form.Group>

                            {/* Vectorization combination selector */}
                            {moduleProps.cloudFile && moduleProps.cloudFile.vectorization_combinations && (
                                <Form.Group className="mb-3">
                                    <LabelWithTooltip tooltip={t('workflow:tooltips.vectorizationCombination')}>
                                        {t('cloud:vectorizationCombination')}
                                    </LabelWithTooltip>
                                    <Form.Select
                                        value={moduleProps.selectedCombination || moduleProps.cloudFile.vectorization_combinations.default_combination || ''}
                                        onChange={(e) => {
                                            const comboId = e.target.value;
                                            const combinations = moduleProps.cloudFile.vectorization_combinations.combinations;
                                            const selectedCombo = combinations[comboId];
                                            if (selectedCombo) {
                                                updateProperty(selectedModuleId, 'selectedCombination', comboId);
                                                updateProperty(selectedModuleId, 'sourceColumn', selectedCombo.source_column);
                                                updateProperty(selectedModuleId, 'targetColumns', selectedCombo.target_columns || []);
                                            }
                                        }}
                                    >
                                        {Object.entries(moduleProps.cloudFile.vectorization_combinations.combinations || {}).map(([comboId, combo]) => (
                                            <option key={comboId} value={comboId}>
                                                {combo.source_column} → {combo.target_columns?.join(', ') || t('workflow:cloud.noTargets')}
                                                {moduleProps.cloudFile.vectorization_combinations.default_combination === comboId ? ` (${t('prompts:default')})` : ''}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            )}

                            {/* Column selection based on selected cloud file */}
                            {moduleProps.cloudFile && moduleProps.selectedCombination && (
                                <>
                                    <Form.Group className="mb-3">
                                        <LabelWithTooltip tooltip={t('workflow:tooltips.sourceTextColumn')}>
                                            {t('cloud:sourceTextColumn')}
                                        </LabelWithTooltip>
                                        <Form.Select
                                            value={moduleProps.sourceColumn || ''}
                                            onChange={(e) => updateProperty(selectedModuleId, 'sourceColumn', e.target.value)}
                                            disabled
                                        >
                                            <option value={moduleProps.sourceColumn}>{moduleProps.sourceColumn}</option>
                                        </Form.Select>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <LabelWithTooltip tooltip={t('workflow:tooltips.targetTextColumns')}>
                                            {t('cloud:targetTextColumns')}
                                        </LabelWithTooltip>
                                        <Select
                                            isMulti
                                            options={(() => {
                                                const combinations = moduleProps.cloudFile.vectorization_combinations?.combinations || {};
                                                const selectedCombo = combinations[moduleProps.selectedCombination];
                                                return (selectedCombo?.target_columns || []).map(col => ({ value: col, label: col }));
                                            })()}
                                            value={(moduleProps.targetColumns || []).map(col => ({ value: col, label: col }))}
                                            onChange={(selected) => {
                                                const cols = selected ? selected.map(item => item.value) : [];
                                                updateProperty(selectedModuleId, 'targetColumns', cols);
                                            }}
                                        />
                                    </Form.Group>
                                </>
                            )}
                        </>
                    )}
                    {(moduleType === 'translation' || moduleType === 'review' || moduleType === 'summary') && (
                        <ModelSettingsPanel
                            moduleId={selectedModuleId}
                            moduleType={moduleType}
                            moduleProps={moduleProps}
                            updateProperty={updateProperty}
                            handleOpenPromptModal={handleOpenPromptModal}
                            applyModelPreset={applyModelPreset}
                        />
                    )}
                </Form>
            </div>
        );
    };

    // Main render
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    return (
        <div className="workflow-container">
            {/* Top Navigation Bar */}
            <Navbar className="workflow-nav" bg="primary" variant="dark" style={{ zIndex: 1000 }}>
                <Container fluid>
                    <Navbar.Brand style={{ fontWeight: 500 }}>🌐 {t('workflow:top.title')}</Navbar.Brand>
                    <div className="ms-2 d-flex align-items-center" style={{ transform: 'scale(0.9)', transformOrigin: 'left center' }}>
                        <LanguageSwitcher size="sm" variant="outline-light" />
                    </div>
                    <Nav className="ms-auto">
                        <span className="text-white me-3" style={{ fontSize: '0.9rem' }}>
                            {t('workflow:top.hello', { name: userInfo.username || 'User' })}
                        </span>
                        <Button variant="outline-light" size="sm" className="me-2" onClick={() => setShowUserProfile(true)}>
                            {t('workflow:top.userProfile')}
                        </Button>
                        <Button variant="outline-light" size="sm" className="me-2" onClick={() => setShowTaskManager(true)}>
                            {t('workflow:top.taskManager')}
                        </Button>
                        <Button variant="outline-light" size="sm" className="me-2" onClick={() => setShowCloudSpace(true)}>{t('workflow:top.cloudSpace')}</Button>
                        <Button
                            variant="outline-light"
                            size="sm"
                            className="me-2"
                            onClick={() => {
                                if (!selectedModuleId) {
                                    notification.showWarning(t('workflow:alerts.selectModuleForPrompts'));
                                    return;
                                }
                                const mod = modules.find(m => m.id === selectedModuleId);
                                if (!mod || !['translation', 'review', 'summary'].includes(mod.type)) {
                                    notification.showWarning(t('workflow:alerts.selectPromptModule'));
                                    return;
                                }
                                setPromptManagerModuleType(mod.type);
                                setShowPromptManager(true);
                            }}
                        >
                            {t('workflow:top.promptManager')}
                        </Button>
                        <Button variant="outline-light" size="sm" onClick={handleLogout}>{t('common:actions.logout')}</Button>
                    </Nav>
                </Container>
            </Navbar>

            {/* Content Section with Resizable Panels */}
            <div
                ref={containerRef}
                className={`workflow-content ${isDragging ? 'dragging' : ''}`}
                style={{
                    gridTemplateColumns: `${sizes[0]}% 4px ${sizes[1]}% 4px ${sizes[2]}%`
                }}
            >
                {/* Panel Controls */}
                <div className="panel-controls">
                    <Button size="sm" variant="outline-secondary" onClick={resetSizes} title={t('workflow:top.resetTitle')}>
                        {t('workflow:top.reset')}
                    </Button>
                </div>
                {/* Left Sidebar: Templates at top, Status at bottom */}
                <div className="module-menu workflow-panel p-3" style={{ display: 'flex', flexDirection: 'column' }}>
                    <h6 className="mb-2">{t('workflow:templates.title')}</h6>
                    <Button variant="outline-secondary" size="sm" className="me-2 import-button"
                        onClick={() => loadTemplate('A')}>
                        {t('workflow:templates.loadA')}
                    </Button>
                    <Button variant="outline-secondary" size="sm" className="me-2 import-button"
                        onClick={() => loadTemplate('B')}>
                        {t('workflow:templates.loadB')}
                    </Button>
                    <Button variant="outline-secondary" size="sm" className="import-button"
                        onClick={() => loadTemplate('C')}>
                        {t('workflow:templates.loadC')}
                    </Button>
                    <div style={{ flexGrow: 1 }} />
                    <div className="mt-3" style={{ height: '50%', overflowY: 'auto' }}>
                        <WorkflowStatusPanel
                            jobStatus={jobStatus}
                            jobProgress={jobProgress}
                            jobDescription={jobDescription}
                            jobError={jobError}
                            isPolling={isPolling}
                            testResultData={testResultData}
                            showTestResult={showTestResult}
                            setShowTestResult={setShowTestResult}
                            isTestMode={false}
                            selectedRow={null}
                            setSelectedRow={() => { }}
                            hideInfo={true}
                            jobId={jobId}
                        />
                    </div>
                </div>

                {/* First Divider */}
                <ResizableDivider
                    index={0}
                    isDragging={isDragging}
                    dragIndex={dragIndex}
                    onMouseDown={handleMouseDown}
                />

                {/* Center Workflow Canvas */}
                <div className="workflow-canvas workflow-panel">
                    <ReactFlowProvider>
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            onConnect={onConnect}
                            onNodesDelete={onNodesDelete}
                            onSelectionChange={onSelectionChange}
                            onPaneClick={onPaneClick}
                            onNodeClick={onNodeClick}
                            onNodeContextMenu={onNodeContextMenu}
                            onEdgeContextMenu={onEdgeContextMenu}
                            onNodeDragStart={onNodeDragStart}
                            onNodeDrag={onNodeDrag}
                            onNodeDragStop={onNodeDragStop}
                            fitView
                            fitViewOptions={{ padding: 0.2 }}
                            defaultViewport={{ x: 0, y: 0, zoom: 1 }}
                            minZoom={0.5}
                            maxZoom={2}
                            nodesDraggable={true}
                            elementsSelectable={true}
                            selectNodesOnDrag={false}
                            style={{
                                width: '100%',
                                height: '100%',
                                background: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px'
                            }}
                        >
                            <Background color="#aaa" gap={16} />
                            <Controls />
                        </ReactFlow>
                    </ReactFlowProvider>
                </div>

                {/* Second Divider */}
                <ResizableDivider
                    index={1}
                    isDragging={isDragging}
                    dragIndex={dragIndex}
                    onMouseDown={handleMouseDown}
                />

                {/* Right Properties Panel */}
                <div className="properties-sidebar workflow-panel p-3">
                    {renderPropertiesPanel()}
                </div>
            </div>

            {/* Bottom actions (Save/Run) */}
            <div className="workflow-footer p-3 bg-white border-top" style={{ textAlign: 'right' }}>
                <Button variant="success" onClick={handleRunWorkflow}>{t('workflow:actions.runWorkflow')}</Button>
            </div>

            {/* Add the modal component */}
            <PromptModal
                show={showPromptModal}
                onHide={() => setShowPromptModal(false)}
                title={promptModalContent.title}
                content={promptModalContent.content}
                onSave={handleSavePrompt}
            />

            {/* Add context menu */}
            {contextMenu && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    onClose={closeContextMenu}
                    menuItems={contextMenu.menuItems}
                />
            )}

            {/* Cloud Space Modal */}
            <CloudSpaceNew
                show={showCloudSpace}
                onHide={() => {
                    setShowCloudSpace(false);
                    setCloudSpaceMode(null);
                    setCloudSpaceModuleTarget(null);
                }}
                onSelectFile={(file) => {
                    try {
                        // Attach selected cloud file to target module
                        const targetId = cloudSpaceModuleTarget;
                        if (!targetId) {
                            notification.showWarning(t('workflow:alerts.selectModuleFirst'));
                            return;
                        }

                        // Check if the file is vectorized for term/memory base
                        const targetModule = modules.find(m => m.id === targetId);
                        if (targetModule && ['termBase', 'memoryBase'].includes(targetModule.type)) {
                            if (!file.vectorized) {
                                notification.showWarning(t('workflow:alerts.selectVectorizedForBase'));
                                return;
                            }

                            // For term/memory base, ensure we have vectorization combinations
                            if (!file.vectorization_combinations) {
                                notification.showWarning(t('workflow:alerts.noCombinations'));
                                return;
                            }
                        }

                        const props = { ...(properties[targetId] || {}) };
                        props.cloudFile = {
                            id: file.id,
                            // Add fileId alias for execution service compatibility
                            fileId: file.id,
                            name: file.name,
                            org_code: file.org_code || null,
                            vectorized: !!file.vectorized,
                            vectorized_source_column: file.vectorized_source_column,
                            vectorized_target_columns: file.vectorized_target_columns,
                            vectorization_combinations: file.vectorization_combinations
                        };

                        // For term/memory base modules, auto-select default combination if available
                        if (['termBase', 'memoryBase'].includes(targetModule?.type)) {
                            if (file.vectorization_combinations?.default_combination) {
                                const defaultCombo = file.vectorization_combinations.combinations[file.vectorization_combinations.default_combination];
                                if (defaultCombo) {
                                    props.selectedCombination = file.vectorization_combinations.default_combination;
                                    props.sourceColumn = defaultCombo.source_column;
                                    props.targetColumns = defaultCombo.target_columns || [];
                                }
                            }
                        }

                        // For fileInput modules, load columns from cloud file
                        if (targetModule?.type === 'fileInput') {
                            props.fileSource = 'cloud';
                            // Use columns embedded in cloud item metadata (populated on upload)
                            const cols = Array.isArray(file.columns) ? file.columns : [];
                            props.columns = cols;
                            props.availableColumns = cols;
                            // If previously selected sourceColumn no longer valid, clear it
                            if (props.sourceColumn && !cols.includes(props.sourceColumn)) {
                                props.sourceColumn = '';
                            }
                        }

                        setProperties(prev => ({ ...prev, [targetId]: props }));
                        setShowCloudSpace(false);
                        setCloudSpaceMode(null);
                        setCloudSpaceModuleTarget(null);
                        notification.showSuccess(t('workflow:messages.cloudSelected'));
                    } catch (e) {
                        console.error('Failed to set cloud file', e);
                        notification.showError(t('workflow:messages.cloudSelectFailed'));
                    }
                }}
                mode={cloudSpaceMode}  // Pass mode to filter vectorized files
            />

            {/* Prompt Manager Modal */}
            <PromptManagerModal
                show={showPromptManager}
                onHide={() => setShowPromptManager(false)}
                moduleType={promptManagerModuleType}
                selectedPromptText={selectedModuleId && properties[selectedModuleId] ? properties[selectedModuleId].prompt : null}
                hasTermBase={modules.some(m => m.type === 'termBase')}
                hasMemoryBase={modules.some(m => m.type === 'memoryBase')}
                onSelectPrompt={(prompt, promptInfo) => {
                    try {
                        const targetModule = modules.find(m => m.type === promptManagerModuleType && m.id === selectedModuleId) ||
                            modules.find(m => m.type === promptManagerModuleType);
                        if (!targetModule) {
                            notification.showWarning(t('workflow:alerts.targetModuleNotFound'));
                            return;
                        }
                        handleSavePrompt(prompt, promptInfo);
                        notification.showSuccess(t('workflow:messages.promptApplied'));
                    } catch (e) {
                        console.error('Failed to apply prompt', e);
                        notification.showError(t('workflow:messages.promptApplyFailed'));
                    }
                }}
            />

            {/* User Profile Modal */}
            <UserProfileModal
                show={showUserProfile}
                onHide={() => setShowUserProfile(false)}
            />

            {/* Task Manager Modal */}
            <TaskManagerModal
                show={showTaskManager}
                onHide={() => setShowTaskManager(false)}
            />

            {/* Version Update Modal */}
            <VersionUpdateModal
                show={showVersionModal}
                onHide={closeVersionModal}
                versionData={versionData}
                onAcknowledge={handleAcknowledge}
            />
        </div>
    );
};

export default Workflow;
